'use client';
import { ValidationOptions, ValidationType, validateField } from '@fshr/shared';
import { useRef, useState } from 'react';

interface FormFieldOptions extends ValidationOptions {
	onBlur?: (event: any) => void;
	onChange?: (event: any) => void;
	analyticsErrorMessages?: Record<string, string>;
}

type EventData = Record<string, any>;

interface FormValue {
	value: string;
	data?: EventData;
}

type FormValues = Record<string, FormValue>;

type FieldIdentifier = { name: string };

type ValidateFunction = (
	newValue?: string,
	data?: EventData,
	getValue?: (fieldName: string) => string
) => [boolean, string, string];

type FieldDef = FieldIdentifier & {
	initialValue: string | FormValue;
	type?: ValidationType;
	options?: FormFieldOptions;
	validate?: ValidateFunction;
};

type ErrorWithAnalytics = { message: string; analyticsMessage: string };

type RegisteredField = {
	validate: ValidateFunction;
	customValidation?: ValidateFunction;
	name: string;
	htmlref: any;
	analyticsErrorMessages?: Record<string, string>;
};

type FieldProps = {
	onBlur: (event: any) => void;
	onChange: (event: any) => void;
	required: boolean;
	name: string;
	errorMessage?: string;
	value?: string;
	checked?: boolean;
	ref: (ref: any) => void;
};

export default function useForm({
	onSubmit,
	onSubmitValidationError,
	shouldScrollOffset,
}: {
	onSubmit: (formValues: Record<string, string>) => void | Promise<void>;
	onSubmitValidationError?: (formErrors: Record<string, ErrorWithAnalytics>) => void;
	shouldScrollOffset?: number;
}) {
	const [formValues, setFormValues] = useState<FormValues>({});
	const [formErrorMessages, setFormErrorMessages] = useState<Record<string, string>>({});
	const registeredFields = useRef<Record<string, RegisteredField>>({});

	const registerField = ({
		name,
		type = 'default',
		options,
		initialValue,
		validate,
	}: FieldDef): FieldProps => {
		const foundField = registeredFields.current[name];
		if (!foundField) {
			//can create the validate function as this shouldn't change
			const validationFunction = (newValue?: string, data?: EventData): [boolean, string, string] =>
				validateField(type, newValue, options, data);

			//add the registered values
			const fieldToRegister: RegisteredField = {
				name,
				htmlref: null,
				validate: validationFunction,
				customValidation: validate,
				analyticsErrorMessages: options?.analyticsErrorMessages,
			};
			//set the form value
			setFormValue(name, initialValue);
			//clear out any errors that may be set for this field
			setFormErrorMessage(name, '');
			//register the field
			registeredFields.current[name] = fieldToRegister;
		}

		const handleValidationFunc = (newValue?: FormValue): void => {
			// if the field has a validate function, use it
			if (registeredFields?.current?.[name]?.customValidation) {
				const [isValid, errorMessage] = registeredFields.current[name].customValidation(
					newValue?.value,
					newValue?.data,
					getValue
				);
				if (!isValid && (newValue?.value || (!newValue?.value && !options?.required))) {
					setFormErrorMessage(name, errorMessage);
					return;
				}
			}
			const [, errorMessage] = registeredFields.current[name].validate(
				newValue?.value,
				newValue?.data
			);
			setFormErrorMessage(name, errorMessage);
		};

		const onBlur = (event: any) => {
			event.preventDefault?.();
			handleValidationFunc(
				type === 'checkbox'
					? { value: event ? 'true' : '' }
					: {
							value: event.currentTarget.value ?? event.target.value,
							data: event.data,
						}
			);
			options?.onBlur?.(event);
		};

		const onChange = (event: any) => {
			event.preventDefault?.();
			const value =
				type === 'checkbox'
					? { value: event ? 'true' : '' }
					: {
							value: event.currentTarget.value ?? event.target.value,
							data: event.data,
						};
			setFormValue(name, value);

			//only validate if this field is in error state
			if (event.forceValidation || (formErrorMessages[name] && formErrorMessages[name] !== '')) {
				handleValidationFunc(value);
			}
			options?.onChange?.(event);
		};

		return {
			onBlur,
			onChange,
			required: options?.required || false,
			name,
			ref: (el: any) => {
				if (registeredFields.current[name]) {
					registeredFields.current[name].htmlref = el;
				}
			},
			...(type === 'checkbox'
				? { checked: formValues[name]?.value === 'true' }
				: { value: formValues[name]?.value as string }),
			errorMessage: formErrorMessages[name],
		};
	};

	const setFormValue = (name: string, value?: string | FormValue) => {
		if (formValues[name]?.value !== value) {
			if (value || value === '') {
				setFormValues(curValues => ({
					...curValues,
					[name]: typeof value === 'string' ? { value } : value,
				}));
			} else {
				setFormValues(curValues => {
					const copyOfValues = { ...curValues };
					delete copyOfValues[name];
					return copyOfValues;
				});
			}
		}
	};
	const setFormErrorMessage = (name: string, value?: any) => {
		if (formErrorMessages[name] !== value) {
			if (value || value === '') {
				setFormErrorMessages(currentMessages => ({
					...currentMessages,
					[name]: value,
				}));
			} else {
				setFormErrorMessages(currentMessages => {
					const copy = { ...currentMessages };
					delete copy[name];
					return copy;
				});
			}
		}
	};

	const unregisterField = ({ name: toRemove }: FieldIdentifier) => {
		delete registeredFields.current[toRemove];
		//set the form value
		setFormValue(toRemove);
		//clear out any errors that may be set for this field
		setFormErrorMessage(toRemove);
	};

	const getValue = (fieldName: string): string => {
		if (!fieldName) {
			throw new Error('Field Name must have a value');
		}

		const registeredField = Object.values(registeredFields.current).find(
			({ name }) => name === fieldName
		);

		if (!registeredField) {
			return '';
		}

		return formValues?.[registeredField.name]?.value ?? '';
	};

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const newFormValues: Record<string, string> = {};
		const newFormErrors: Record<string, string> = {};
		const newFormErrorsWithAnalytics: Record<string, ErrorWithAnalytics> = {};
		let scrollToTarget: HTMLElement | null = null;
		let formValid = true;
		Object.values(registeredFields.current).forEach(
			({ name, htmlref, validate, customValidation, analyticsErrorMessages }) => {
				const newValue = formValues[name];
				newFormValues[name] = newValue?.value;
				const [isValid, errorMessage, errorType] = validate(
					newValue?.value,
					newValue?.data,
					getValue
				);
				const [customValidationIsValid, customValidationErrorMessage, customValidationErrorType] =
					customValidation?.(newValue.value, newValue.data, getValue) || [true, '', ''];
				if (!isValid || !customValidationIsValid) {
					formValid = false;
					newFormErrorsWithAnalytics[name] = {
						message: errorMessage || customValidationErrorMessage,
						analyticsMessage:
							analyticsErrorMessages?.[errorType || customValidationErrorType] || '',
					};
					if (!scrollToTarget) {
						scrollToTarget = htmlref;
						const targetRect = scrollToTarget?.getBoundingClientRect();
						if (targetRect) {
							const modalRect = document.getElementById('modal')?.getBoundingClientRect();
							const navOffset =
								shouldScrollOffset ||
								document.getElementById('header-nav')?.getBoundingClientRect().height ||
								0;

							const containerTop = modalRect?.top || navOffset;
							const containerBottom = modalRect?.bottom || window.innerHeight;

							if (targetRect.top < containerTop || targetRect.bottom > containerBottom) {
								scrollToTarget?.scrollIntoView({
									behavior: 'smooth',
									block: 'center',
								});
							}
						}
					}
				}
				newFormErrors[name] = errorMessage || customValidationErrorMessage;
			}
		);
		if (formValid) {
			if (onSubmit) {
				setFormErrorMessages({});
				onSubmit(newFormValues);
			} else {
				throw new Error('Submit function missing');
			}
		} else {
			onSubmitValidationError?.(newFormErrorsWithAnalytics);
			setFormErrorMessages(newFormErrors);
		}
	};
	return { registerField, unregisterField, handleSubmit, setFormErrorMessage, getValue };
}
